import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { apiRequest } from '../../services/api';
import DashboardLayout from '../../components/Dashboard/DashboardLayout';
import MetricsCard from '../../components/Shared/MetricsCard';
import ProgressBar from '../../components/Shared/ProgressBar';
import DataTable from '../../components/Shared/DataTable';
import { schoolAdminNavItems } from '../../constants/navigation';

/**
 * SchoolAdminDashboard - Main dashboard for school administrators
 * Shows school overview, teacher management, license status
 * Allows creating teacher activation codes
 */
const SchoolAdminDashboard = () => {
  const { currentUser } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateCodeModal, setShowCreateCodeModal] = useState(false);
  const [teacherFormData, setTeacherFormData] = useState({
    fullName: '',
    subject: '',
    gradeLevel: ''
  });
  const [createdCode, setCreatedCode] = useState(null);
  const [createLoading, setCreateLoading] = useState(false);

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        setLoading(true);
        const data = await apiRequest('/api/school-admin/dashboard', 'GET');
        setDashboardData(data);
        setTeachers(data.teachers || []);
      } catch (err) {
        console.error('Failed to load dashboard:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, []);

  // Create teacher activation code
  const handleCreateCode = async () => {
    if (!teacherFormData.fullName || !teacherFormData.subject || !teacherFormData.gradeLevel) {
      alert('Please fill in all required fields (name, subject, and grade level)');
      return;
    }

    setCreateLoading(true);
    try {
      const response = await apiRequest('/api/school-admin/codes/teacher/create', 'POST', {
        full_name: teacherFormData.fullName,
        subject: teacherFormData.subject,
        grade_level: teacherFormData.gradeLevel
      });
      setCreatedCode(response);
      // Refresh teacher list
      const data = await apiRequest('/api/school-admin/dashboard', 'GET');
      setTeachers(data.teachers || []);
    } catch (err) {
      alert(`Failed to create code: ${err.message}`);
    } finally {
      setCreateLoading(false);
    }
  };

  // Export school data
  const handleExportData = async () => {
    try {
      // This would download a CSV file
      const response = await apiRequest('/api/school-admin/export', 'GET');
      // Trigger download
      const blob = new Blob([response], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `school-data-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
    } catch (err) {
      alert(`Export failed: ${err.message}`);
    }
  };

  // Teacher table columns
  const teacherColumns = [
    {
      key: 'full_name',
      label: 'Teacher',
      sortable: true,
      render: (teacher) => (
        <div>
          <p className="font-medium text-gray-900">{teacher.full_name}</p>
          <p className="text-xs text-gray-500">{teacher.email}</p>
        </div>
      )
    },
    {
      key: 'subject',
      label: 'Subject',
      sortable: true
    },
    {
      key: 'grade_level',
      label: 'Grade',
      sortable: true,
      render: (teacher) => (
        <span className="capitalize">{teacher.grade_level || 'N/A'}</span>
      )
    },
    {
      key: 'student_count',
      label: 'Students',
      sortable: true,
      render: (teacher) => (
        <span className="font-medium">{teacher.student_count || 0}</span>
      )
    },
    {
      key: 'class_count',
      label: 'Classes',
      sortable: true,
      render: (teacher) => (
        <span className="font-medium">{teacher.class_count || 0}</span>
      )
    },
    {
      key: 'activation_code',
      label: 'Activation Code',
      render: (teacher) => {
        if (teacher.activation_code_used) {
          return <span className="text-green-600 text-xs">✓ Activated</span>;
        }
        return (
          <button
            onClick={() => {
              navigator.clipboard.writeText(teacher.activation_code);
              alert(`Code copied: ${teacher.activation_code}`);
            }}
            className="text-indigo-600 hover:text-indigo-800 font-medium text-xs flex items-center"
            title={teacher.activation_code}
          >
            📋 Copy Code
          </button>
        );
      }
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (teacher) => {
        if (teacher.is_active === false) {
          return <span className="text-gray-500">Inactive</span>;
        }
        if (teacher.activation_code_used) {
          return <span className="text-green-600 font-medium">Active</span>;
        }
        return <span className="text-yellow-600">Pending Activation</span>;
      }
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (teacher) => (
        <button
          onClick={() => window.location.href = `/school-admin/teacher/${teacher.id}`}
          className="text-indigo-600 hover:text-indigo-800 font-medium text-sm"
        >
          View Details →
        </button>
      )
    }
  ];

  if (loading) {
    return (
      <DashboardLayout
        title="School Admin Dashboard"
        navItems={schoolAdminNavItems}
        userRole={currentUser?.user_type || 'school-admin'}
        userName={currentUser?.full_name || 'Admin'}
      >
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          <span className="ml-3 text-gray-600">Loading dashboard...</span>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout
        title="School Admin Dashboard"
        navItems={schoolAdminNavItems}
        userRole={currentUser?.user_type || 'school-admin'}
        userName={currentUser?.full_name || 'Admin'}
      >
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          Error loading dashboard: {error}
        </div>
      </DashboardLayout>
    );
  }

  const licenseUsagePercent = dashboardData?.license_status
    ? Math.round((dashboardData.license_status.licenses_used / dashboardData.license_status.license_count) * 100)
    : 0;

  return (
    <DashboardLayout
      title="School Admin Dashboard"
      navItems={schoolAdminNavItems}
      userRole={currentUser?.user_type || 'school-admin'}
      userName={currentUser?.full_name || 'Admin'}
    >
      {/* Welcome Section */}
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-gray-900">
          {dashboardData?.school?.school_name || 'Your School'}
        </h2>
        <p className="text-gray-600 mt-1">
          {dashboardData?.school?.district_name || 'School District'}
        </p>
      </div>

      {/* Metrics Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <MetricsCard
          title="Teachers"
          value={dashboardData?.school_metrics?.total_teachers || 0}
          subtitle="on the platform"
          icon="👨‍🏫"
        />
        <MetricsCard
          title="Students"
          value={dashboardData?.school_metrics?.total_students || 0}
          subtitle="enrolled"
          icon="👥"
        />
        <MetricsCard
          title="Total Sessions"
          value={dashboardData?.school_metrics?.total_sessions || 0}
          subtitle="all time"
          icon="💬"
        />
        <MetricsCard
          title="License Usage"
          value={`${licenseUsagePercent}%`}
          subtitle={`${dashboardData?.license_status?.licenses_used || 0} of ${dashboardData?.license_status?.license_count || 0}`}
          trend={licenseUsagePercent >= 90 ? '⚠️ High' : '✓ Good'}
          trendColor={licenseUsagePercent >= 90 ? 'yellow' : 'green'}
          icon="📊"
        />
      </div>

      {/* License Status */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">License Status</h3>
        <ProgressBar
          current={dashboardData?.license_status?.licenses_used || 0}
          total={dashboardData?.license_status?.license_count || 100}
          warningThreshold={90}
          label={`${dashboardData?.license_status?.licenses_used || 0} / ${dashboardData?.license_status?.license_count || 100} licenses used`}
        />
        <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-600">Available Licenses</p>
            <p className="text-2xl font-bold text-green-600">
              {(dashboardData?.license_status?.license_count || 0) - (dashboardData?.license_status?.licenses_used || 0)}
            </p>
          </div>
          <div>
            <p className="text-gray-600">Subscription Expires</p>
            <p className="text-lg font-semibold text-gray-900">
              {dashboardData?.license_status?.subscription_end_date
                ? new Date(dashboardData.license_status.subscription_end_date).toLocaleDateString()
                : 'N/A'}
            </p>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => setShowCreateCodeModal(true)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
          >
            + Create Teacher Code
          </button>
          <button
            onClick={handleExportData}
            className="px-4 py-2 bg-white text-indigo-600 border-2 border-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors font-medium"
          >
            📥 Export School Data
          </button>
          <button
            onClick={() => window.location.href = '/school-admin/analytics'}
            className="px-4 py-2 bg-white text-indigo-600 border-2 border-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors font-medium"
          >
            📈 View Analytics
          </button>
        </div>
      </div>

      {/* Teacher List */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold text-gray-900">Teachers</h3>
          <span className="text-sm text-gray-600">
            {teachers.length} teacher{teachers.length !== 1 ? 's' : ''}
          </span>
        </div>
        <DataTable
          columns={teacherColumns}
          data={teachers}
          emptyMessage="No teachers yet. Create activation codes to invite teachers!"
        />
      </div>

      {/* Create Teacher Code Modal */}
      {showCreateCodeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Create Teacher Activation Code
            </h3>

            {!createdCode ? (
              <>
                <div className="space-y-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Teacher Name*
                    </label>
                    <input
                      type="text"
                      placeholder="Mrs. Jane Doe"
                      value={teacherFormData.fullName}
                      onChange={(e) => setTeacherFormData({...teacherFormData, fullName: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Subject/Grade*
                    </label>
                    <input
                      type="text"
                      placeholder="5th Grade Math"
                      value={teacherFormData.subject}
                      onChange={(e) => setTeacherFormData({...teacherFormData, subject: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Grade Level*
                    </label>
                    <select
                      value={teacherFormData.gradeLevel}
                      onChange={(e) => setTeacherFormData({...teacherFormData, gradeLevel: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500 bg-white"
                      required
                    >
                      <option value="">Select grade level...</option>
                      <option value="elementary">Elementary (K-5)</option>
                      <option value="middle">Middle School (6-8)</option>
                    </select>
                  </div>
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={handleCreateCode}
                    disabled={createLoading}
                    className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {createLoading ? 'Creating...' : 'Create Code'}
                  </button>
                  <button
                    onClick={() => setShowCreateCodeModal(false)}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-green-600 font-semibold mb-3">
                  ✓ Code created successfully!
                </p>
                <div className="bg-gray-50 rounded p-4 mb-4">
                  <p className="text-sm text-gray-600 mb-2">Activation Code:</p>
                  <p className="font-mono text-xl text-indigo-600 font-bold">{createdCode.activation_code}</p>
                </div>
                <p className="text-sm text-gray-600 mb-1">
                  <strong>Teacher:</strong> {createdCode.teacher_name || teacherFormData.fullName}
                </p>
                <p className="text-sm text-gray-600 mb-4">
                  <strong>Expires:</strong> {createdCode.expires_at ? new Date(createdCode.expires_at).toLocaleDateString() : 'N/A'}
                </p>
                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(createdCode.activation_code);
                      alert('Code copied to clipboard!');
                    }}
                    className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                  >
                    📋 Copy Code
                  </button>
                  <button
                    onClick={() => {
                      setShowCreateCodeModal(false);
                      setCreatedCode(null);
                      setTeacherFormData({ fullName: '', subject: '', gradeLevel: '' });
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                  >
                    Done
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default SchoolAdminDashboard;
